import {
  useContextAreaRead,
  useContextPointRead,
  useContextSources,
} from "@/hooks/useReferenceData"
import { useProjectSettings } from "@/hooks/useProjectSettings"
import { useNetwork } from "@/hooks/useNetwork"
import { useMiniSearch } from "@/components/project/ProjectDataSheet/useMiniSearch"
import { aggregateContextAreasByCommunity } from "@fieldbook/common/utils"
import { TaxonName } from "@fieldbook/common/components"
import { isDBCASiteSourceKind } from "@fieldbook/common/types"
import { CommunityMapView } from "@/components/project/CommunityMapView"
import {
  CONSERVATION_BADGE_CLASSES,
  CONSERVATION_ORDER,
  formatConservationCode,
  formatContextSourceLabel,
  getConservationGroupName,
} from "@fieldbook/common/utils"
import { Badge } from "@fieldbook/ui/badge"
import { Button } from "@fieldbook/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@fieldbook/ui/dropdown-menu"
import { Switch } from "@fieldbook/ui/switch"
import { cn } from "@fieldbook/ui/utils"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowLeft,
  CheckCircle,
  Group,
  MapPin,
  RefreshCw,
  SortAsc,
  SortDesc,
} from "lucide-react"
import { useMemo, useState } from "react"
import {
  SearchButton,
  SearchInputProvider,
  SearchTextField,
} from "../species/-components/SearchInput"

export const Route = createFileRoute(
  "/_private/projects/$id/data/context/$sourceId",
)({
  // Point sources use species grouping; TEC/PEC sources use the area detail
  // branch below. Linked organisation documents live at context/document/$documentId.
  component: SpeciesSourceDetail,
})

type SortDirection = "asc" | "desc"
type GroupBy = "conservation" | null

type ContextSpeciesData = {
  taxon: string
  conservationCode?: string
  speciesId?: string
  locationCount: number
}

type TecPecAreaListItem = ReturnType<
  typeof aggregateContextAreasByCommunity
>[number] & {
  searchText: string
}

function SpeciesSourceDetail() {
  const { id: projectId, sourceId } = Route.useParams()
  const navigate = Route.useNavigate()

  const { data: sources } = useContextSources(projectId)
  const { settings, updateSettings } = useProjectSettings(projectId)
  const { isOnline } = useNetwork()

  const source = sources?.find((s) => s.id === sourceId)

  const isTecPec = source?.kind === "dbca_tec_pec"
  const isSiteSource = source ? isDBCASiteSourceKind(source.kind) : false
  const syncedIds = settings.syncedContextSourceIds ?? []
  const mapIds = settings.mapContextSourceIds ?? []
  const isSynced = syncedIds.includes(sourceId)
  const isOnMap = mapIds.includes(sourceId)
  const pointRead = useContextPointRead(
    projectId,
    !isTecPec && (isOnMap || isSynced) ? [sourceId] : [],
    syncedIds,
    isOnline,
  )
  const areaRead = useContextAreaRead(
    projectId,
    isTecPec && (isOnMap || isSynced) ? [sourceId] : [],
    syncedIds,
    isOnline,
  )
  const areaRoles = settings.mapContextAreaRoles?.[sourceId] ?? {
    boundary: true,
    buffer: true,
  }

  const toggleSync = (next: boolean) => {
    updateSettings({
      syncedContextSourceIds: next
        ? [...new Set([...syncedIds, sourceId])]
        : syncedIds.filter((id) => id !== sourceId),
    })
  }

  const toggleOnMap = (next: boolean) => {
    updateSettings({
      mapContextSourceIds: next
        ? [...new Set([...mapIds, sourceId])]
        : mapIds.filter((id) => id !== sourceId),
    })
  }

  const toggleAreaRole = (role: "boundary" | "buffer", next: boolean) => {
    updateSettings({
      mapContextAreaRoles: {
        ...settings.mapContextAreaRoles,
        [sourceId]: { ...areaRoles, [role]: next },
      },
    })
  }

  // Filter/sort/group state
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc")
  const [groupBy, setGroupBy] = useState<GroupBy>(null)

  const features = pointRead.tpfl.features.concat(
    pointRead.waherb.features,
    pointRead.boranga.features,
    pointRead.tpfa.features,
    pointRead.bcRoost.features,
    pointRead.bcBreeding.features,
  )
  const areas = areaRead.data.features
  const areaSummaries = useMemo(
    () => aggregateContextAreasByCommunity(areas ?? []),
    [areas],
  )
  const rejectedAreaCount = areaRead.rejected.length
  const isAreaQueryError = areaRead.error !== null
  const areaQueryError = areaRead.error
  const isAreaLoading = areaRead.isLoading
  const isAreaUnavailableOffline = areaRead.unavailableOffline.length > 0
  const [areaSearch, setAreaSearch] = useState("")
  const [selectedCommunityId, setSelectedCommunityId] = useState<string | null>(
    null,
  )
  const filteredAreas = useMemo<TecPecAreaListItem[]>(() => {
    const query = areaSearch.trim().toLowerCase()
    return areaSummaries
      .map((summary) => ({
        ...summary,
        searchText: [
          summary.communityId,
          summary.communityName,
          summary.areas.flatMap((area) => [
            area.properties.label,
            area.properties.class_code,
            area.properties.COM_NAME,
            area.properties.COM_ID,
            area.properties.STATE_CATG,
            area.properties.COMM_CATG,
            area.properties.summary_description,
            area.properties.description,
          ]),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase(),
      }))
      .filter((summary) => !query || summary.searchText.includes(query))
  }, [areaSearch, areaSummaries])

  const selectedCommunity = areaSummaries.find(
    (summary) => summary.communityId === selectedCommunityId,
  )
  const speciesData = useMemo((): ContextSpeciesData[] => {
    const map: Record<string, ContextSpeciesData> = {}
    features.forEach(({ properties }) => {
      const { taxon, conservationCode, speciesId } = properties
      if (map[taxon]) {
        map[taxon].locationCount++
      } else {
        map[taxon] = { taxon, conservationCode, speciesId, locationCount: 1 }
      }
    })
    return Object.values(map)
  }, [features])

  const { searchResults, ...searchProps } = useMiniSearch(speciesData, {
    fields: ["taxon"],
    idField: "taxon",
    searchOptions: { fuzzy: 0.2 },
  })

  const { results, filteredCount } = useMemo(() => {
    const filtered = searchResults ?? speciesData
    const sorted = [...filtered].sort((a, b) => {
      const comparison = a.taxon.localeCompare(b.taxon)
      return sortDirection === "asc" ? comparison : -comparison
    })

    if (groupBy === "conservation") {
      const grouped: Record<string, ContextSpeciesData[]> = {}
      sorted.forEach((species) => {
        const groupName = getConservationGroupName(species.conservationCode)
        if (!grouped[groupName]) grouped[groupName] = []
        grouped[groupName].push(species)
      })
      const sortedGroups: Record<string, ContextSpeciesData[]> = {}
      Object.keys(grouped)
        .sort((a, b) => {
          const orderA =
            CONSERVATION_ORDER[
              a === "Threatened" ? "T" : a.replace("Priority ", "")
            ] ?? 99
          const orderB =
            CONSERVATION_ORDER[
              b === "Threatened" ? "T" : b.replace("Priority ", "")
            ] ?? 99
          return orderA - orderB
        })
        .forEach((name) => {
          sortedGroups[name] = grouped[name]
        })
      return { results: sortedGroups, filteredCount: sorted.length }
    }

    return { results: { "": sorted }, filteredCount: sorted.length }
  }, [searchResults, speciesData, sortDirection, groupBy])

  const title = source
    ? formatContextSourceLabel(
        source.kind,
        source.source_filename ?? source.source_org_name,
      )
    : "Context source"

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="mb-2 mt-2 space-y-2">
        <button
          onClick={() =>
            navigate({
              to: "/projects/$id/data/context",
              params: { id: projectId },
            })
          }
          className="text-muted-foreground flex items-center gap-1 text-sm focus-visible:outline-0"
        >
          <ArrowLeft size={12} />
          <span>Context</span>
        </button>
        <span className="text-lg font-medium">{title}</span>
        {source && (
          <p className="text-muted-foreground text-xs">
            {isTecPec
              ? `${source.feature_count} polygons`
              : isSiteSource
                ? `${source.feature_count} sites`
                : `${source.feature_count} locations • ${source.species_count} species`}
          </p>
        )}
      </div>

      {/* Config */}
      <div className="border-muted mb-4 flex flex-col gap-3 border-y py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw size={16} className="text-muted-foreground" />
            <div className="flex flex-col">
              <span className="text-sm">Sync offline</span>
              <span className="text-muted-foreground text-xs">
                Download this source for offline use
              </span>
            </div>
          </div>
          <Switch checked={isSynced} onCheckedChange={toggleSync} />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-muted-foreground" />
            <div className="flex flex-col">
              <span className="text-sm">Show on map</span>
              <span className="text-muted-foreground text-xs">
                {isTecPec
                  ? "Display these polygons on the project map"
                  : "Display these locations on the project map"}
              </span>
            </div>
          </div>
          <Switch checked={isOnMap} onCheckedChange={toggleOnMap} />
        </div>
        {isTecPec && (
          <div className="border-muted flex flex-col gap-2 border-t pt-3">
            <span className="text-muted-foreground text-xs">Map layers</span>
            <label className="flex items-center justify-between text-sm">
              Boundary
              <Switch
                checked={areaRoles.boundary}
                onCheckedChange={(next) => toggleAreaRole("boundary", next)}
              />
            </label>
            <label className="flex items-center justify-between text-sm">
              Buffered area
              <Switch
                checked={areaRoles.buffer}
                onCheckedChange={(next) => toggleAreaRole("buffer", next)}
              />
            </label>
          </div>
        )}
      </div>

      {/* Occurrences (grouped species) */}
      {!isSynced && !isOnMap ? (
        <div className="text-muted-foreground py-6 text-center text-sm">
          Enable sync to view this source's records offline.
        </div>
      ) : isTecPec && selectedCommunity ? (
        <CommunityMapView
          community={selectedCommunity}
          onClose={() => setSelectedCommunityId(null)}
        />
      ) : isTecPec ? (
        isAreaLoading ? (
          <div className="text-muted-foreground py-6 text-center text-sm">
            Loading polygons…
          </div>
        ) : isAreaQueryError ? (
          <div className="text-destructive py-6 text-center text-sm">
            Unable to load polygons
            {areaQueryError?.message ? `: ${areaQueryError.message}` : "."}
          </div>
        ) : isAreaUnavailableOffline ? (
          <div className="text-muted-foreground py-6 text-center text-sm">
            This source is enabled on the map, but its polygons are not
            available offline. Connect to the internet or enable Sync offline.
          </div>
        ) : !areas || areas.length === 0 ? (
          <div className="flex flex-col gap-2 py-6 text-center text-sm">
            <span className="text-muted-foreground">
              No polygons available for this source.
            </span>
            {rejectedAreaCount > 0 && (
              <div className="border-destructive/40 bg-destructive/10 text-destructive rounded border p-2 text-left text-xs">
                Some polygons could not be loaded ({rejectedAreaCount}{" "}
                rejected).
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs">
                {areaSummaries.length} communities • {areas.length} polygons •{" "}
                {areaSummaries.reduce(
                  (count, summary) => count + summary.instanceCount,
                  0,
                )}{" "}
                instances
              </span>
              <input
                className="border-muted bg-background w-48 rounded border px-2 py-1 text-xs"
                placeholder="Search communities or IDs"
                value={areaSearch}
                onChange={(event) => setAreaSearch(event.target.value)}
              />
            </div>
            {rejectedAreaCount > 0 && (
              <div className="border-destructive/40 bg-destructive/10 text-destructive rounded border p-2 text-xs">
                Some polygons could not be loaded ({rejectedAreaCount}{" "}
                rejected).
              </div>
            )}
            {filteredAreas.map((summary) => (
              <button
                key={summary.communityId}
                onClick={() => setSelectedCommunityId(summary.communityId)}
                type="button"
                className="border-muted w-full border-t p-2 text-left text-sm"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">
                    {summary.communityName || summary.communityId}
                  </span>
                  <Badge variant="secondary">
                    {summary.instanceCount} instances
                  </Badge>
                </div>
                <div className="text-muted-foreground mt-1 space-y-0.5 text-xs">
                  <div>Community ID: {summary.communityId}</div>
                  <div>
                    {summary.boundaryCount} boundaries • {summary.bufferCount}{" "}
                    buffered areas
                  </div>
                  {(summary.areas[0]?.properties.STATE_CATG ||
                    summary.areas[0]?.properties.COMM_CATG) && (
                    <div>
                      Category:{" "}
                      {summary.areas[0].properties.STATE_CATG ||
                        summary.areas[0].properties.COMM_CATG}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )
      ) : speciesData.length === 0 ? (
        <div className="text-muted-foreground py-6 text-center text-sm">
          {isSynced
            ? "Syncing… no records yet."
            : !isOnline
              ? "This source is enabled on the map, but its records are not available offline. Connect to the internet or enable Sync offline."
              : pointRead.isLoading
                ? "Loading records…"
                : "No records available for this source."}
        </div>
      ) : (
        <>
          <div className="mb-2 space-y-1">
            <SearchInputProvider {...searchProps}>
              <div className="flex w-full items-center justify-end gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="default">
                      <Group aria-label="Group" size={14} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setGroupBy(null)}>
                      No grouping {groupBy === null && <CheckCircle />}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setGroupBy("conservation")}
                    >
                      Conservation Status{" "}
                      {groupBy === "conservation" && <CheckCircle />}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="default">
                      {sortDirection === "desc" ? (
                        <SortDesc size={14} />
                      ) : (
                        <SortAsc size={14} />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setSortDirection("asc")}>
                      Name (Asc) {sortDirection === "asc" && <CheckCircle />}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setSortDirection("desc")}>
                      Name (Desc) {sortDirection === "desc" && <CheckCircle />}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <SearchButton />
              </div>
              <SearchTextField />
            </SearchInputProvider>
            <span className="text-xs">
              {filteredCount} of {speciesData.length} species •{" "}
              {features.length} locations
            </span>
          </div>

          <div className="flex flex-col">
            {Object.entries(results).map(([groupName, groupSpecies]) => (
              <div key={groupName}>
                {groupName && (
                  <div className="bg-muted/40 text-accent-foreground px-2 py-1 text-sm">
                    {groupName}
                  </div>
                )}
                {groupSpecies.map((species, index) => (
                  <Link
                    key={species.taxon}
                    to={
                      species.speciesId
                        ? "/projects/$id/data/species/$speciesId"
                        : "/projects/$id/data/context/species/$taxon"
                    }
                    params={{
                      id: projectId,
                      speciesId: species.speciesId,
                      taxon: species.taxon,
                    }}
                    className={cn(
                      "border-muted flex items-center justify-between border-t p-2",
                      index % 2 === 0 && "bg-muted/10",
                    )}
                  >
                    <div className="flex flex-col gap-1">
                      <TaxonName
                        name={species.taxon}
                        className="text-foreground"
                      />
                      <div className="flex items-center gap-2">
                        {species.conservationCode && !groupBy && (
                          <Badge
                            className={cn(
                              "text-xs",
                              CONSERVATION_BADGE_CLASSES[
                                species.conservationCode
                              ],
                            )}
                          >
                            {formatConservationCode(species.conservationCode)}
                          </Badge>
                        )}
                        <span className="text-muted-foreground text-xs">
                          {species.locationCount} location
                          {species.locationCount !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ))}

            {filteredCount === 0 && (
              <div className="p-4 text-center">
                {searchProps.searchValue ? (
                  <p>No results for "{searchProps.searchValue}"</p>
                ) : (
                  <p>No species in this source</p>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
